/**
 * TE-2038: resolving the lift a lift-scoped page is about.
 *
 * The Alerts & Maintenance History page (`/liftMaintenance`) and Add Maintenance
 * (`/addMaintenance`) are both reached by `navigate(path, { state: { liftId } })`
 * and originally read the lift id ONLY from `location.state`. React Router keeps
 * that in `window.history.state.usr`, which the browser does restore across a
 * reload — so it mostly worked, and that is why the bug looked intermittent.
 *
 * It is not durable though. Any navigation to the same URL that does not forward
 * `state` replaces the history entry with one that has none, and from then on the
 * page is blank: `DataMain`'s fetch effect is guarded by `if (liftId)`, so with no
 * id nothing is requested and "Lift Name :" renders empty. A URL with the lift in
 * the query string survives reloads, bookmarks and copy-paste, none of which
 * history state does.
 *
 * So: read `state` first (it is what every call site already sends), fall back to
 * `?liftId=`, and have the page mirror whatever it resolved into the query string
 * so the next reload has something to read.
 */

export const LIFT_ID_PARAM = "liftId";

/** Treats null/undefined/"" as absent, and coerces to string for URL use. */
function normalise(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === "" ? null : text;
}

/**
 * The lift id for a lift-scoped page, or null when there is none to be had.
 *
 * `state` wins over the query string so an explicit in-app navigation always
 * beats a stale URL — click lift B from the grid while sitting on lift A's page
 * and you get B, not A.
 *
 * @param {object} location A react-router location.
 * @returns {?string}
 */
export function resolveLiftId(location) {
  const fromState = normalise(location?.state?.liftId);
  if (fromState !== null) return fromState;
  try {
    return normalise(new URLSearchParams(location?.search || "").get(LIFT_ID_PARAM));
  } catch (e) {
    // Malformed search string — treat as absent rather than breaking the page.
    return null;
  }
}

/**
 * The `navigate` target that adds/corrects `?liftId=` on the current URL, or
 * null when the URL already says the right thing and no navigation is needed.
 *
 * Returning null is what stops this from looping: the page calls navigate only
 * while the URL disagrees with the resolved id, and the resulting location
 * agrees, so the next pass is a no-op.
 *
 * Other query params and the hash are preserved — deep links arrive with
 * `?type=` / `?date=` and the breadcrumb relies on them.
 *
 * @returns {?{to: object, options: object}}
 */
export function buildLiftIdUrlSync(location, liftId) {
  const id = normalise(liftId);
  if (id === null) return null;

  let params;
  try {
    params = new URLSearchParams(location?.search || "");
  } catch (e) {
    params = new URLSearchParams();
  }
  if (params.get(LIFT_ID_PARAM) === id) return null;

  params.set(LIFT_ID_PARAM, id);
  const search = params.toString();
  return {
    to: {
      pathname: location?.pathname,
      search: search ? `?${search}` : "",
      hash: location?.hash || "",
    },
    // replace, not push: this is the same page, so a pushed entry would make the
    // back button need two presses to leave. state is forwarded so the id stays
    // available by both routes.
    options: { replace: true, state: location?.state },
  };
}
